import { TextField, TextFieldProps } from '@mui/material'
import { LocalizationProvider, DateRangePicker, DateRange, RangePosition } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import dayjs from 'dayjs'
import _ from 'lodash'
import React, { PropsWithChildren, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import 'temporal-polyfill/global'

import {
  is_same_date,
  stringToZonedDateTime,
  ITemporalRange,
  getJurisdictionTimezone,
} from 'src/commons/temporalHelpers'

import CollapsibleFilter, { ICollapsibleFilterProps } from '../BaseFilter/CollapsibleFilter'

import { getDayjsValues, normalize } from './utils'

export interface ITimeRangeFilterProProps extends Omit<ICollapsibleFilterProps, 'values' | 'displayCheckbox'> {
  disableFuture?: boolean
  format?: 'L' | 'L LT'
  maximum?: Temporal.ZonedDateTime
  minimum?: Temporal.ZonedDateTime
  missingValue?: boolean
  normalize?: boolean
  onBothChanged: (valueFrom: Temporal.ZonedDateTime | undefined, valueTo: Temporal.ZonedDateTime | undefined) => void
  onFromChanged: (value: Temporal.ZonedDateTime | undefined) => void
  onToChanged: (value: Temporal.ZonedDateTime | undefined) => void
  values?: ITemporalRange
}

function CustomTextField(
  props: PropsWithChildren<TextFieldProps> & {
    updateDateRangeFromField: (label: RangePosition, value: unknown) => void
  }
) {
  const { updateDateRangeFromField, ...other } = props
  const [didInput, setDidInput] = useState<boolean>(false)

  return (
    <TextField
      {...other}
      onInput={(_) => {
        setDidInput(true)
      }}
      InputProps={{
        id: props.id,
        sx: { overflowX: 'scroll' }, // AM and PM might be hidden otherwise
      }}
      onBlur={(_) => {
        // Condition to trigger the updateDateRangeFromField only if the user inputed the
        // date manually and not through the DateRangePicker interface
        if (didInput) {
          updateDateRangeFromField(other.name as RangePosition, other.value)
          setDidInput(false)
        }
      }}
    />
  )
}

export default function TimeRangeFilterPro({
  values,
  format,
  missingValue,
  disableFuture,
  ...props
}: ITimeRangeFilterProProps) {
  const { t } = useTranslation()

  const shouldNormalize = useMemo(() => props.normalize || format === 'L', [format])
  const dateRange: DateRange<dayjs.Dayjs> = useMemo(() => {
    const dayjsValues = getDayjsValues(values)
    return [dayjsValues.from, dayjsValues.to] as DateRange<dayjs.Dayjs>
  }, [values])

  const minmax: DateRange<dayjs.Dayjs> = useMemo(() => {
    const dayjsValues = getDayjsValues({ from: props.minimum, to: props.maximum })
    return [dayjsValues.from, dayjsValues.to] as DateRange<dayjs.Dayjs>
  }, [props.minimum, props.maximum])

  const updateDateRange = (value: DateRange<dayjs.Dayjs>) => {
    if (shouldNormalize) {
      value = normalize(value)
    }

    props.onBothChanged(
      value[0] ? stringToZonedDateTime(value[0].format()) : undefined,
      value[1] ? stringToZonedDateTime(value[1].format()) : undefined
    )
  }

  const updateDateRangeFromField = (position: RangePosition, value: unknown) => {
    if (!value || typeof value !== 'string' || value === '') {
      return
    }

    if (position === 'start') {
      props.onFromChanged(value ? stringToZonedDateTime(value) : undefined)
    } else if (position === 'end') {
      props.onToChanged(value ? stringToZonedDateTime(value) : undefined)
    }
  }

  const subtitle = useMemo(() => {
    if (dateRange[0] && dateRange[1]) {
      return t('filters.date.rangeSubtitle', {
        start: dateRange[0].tz(getJurisdictionTimezone()).format(format),
        end: dateRange[1].tz(getJurisdictionTimezone()).format(format),
      })
    }
    return undefined
  }, [dateRange, format])

  return (
    <CollapsibleFilter
      filterId={props.filterId}
      defaultExpanded={props.defaultExpanded}
      displayCheckbox={false}
      title={props.title}
      subtitle={subtitle}
      errorMessage={missingValue ? t('filters.missing_value') : undefined}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs.locale()}>
        <DateRangePicker
          format={format}
          value={dateRange}
          onAccept={(value) => {
            if (!is_same_date(dateRange, value)) {
              updateDateRange(value)
            }
          }}
          disableFuture={disableFuture}
          // Name is a property of HTML input tag and could conflict if TimeRangeFilterPro is used in a form
          slotProps={{ textField: ({ position }) => ({ name: position }) }}
          slots={{
            textField: (props) => {
              return <CustomTextField {...props} updateDateRangeFromField={updateDateRangeFromField} />
            },
          }}
          minDate={minmax[0]}
          maxDate={minmax[1]}
        />
      </LocalizationProvider>
    </CollapsibleFilter>
  )
}
